import { useCallback, useEffect, useState } from 'react';

import { Check, Copy, Eye, EyeOff, Loader2, Radio, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useGenerateWebhookToken, useWebhookTestPayload } from '@/hooks/useWorkflows';

interface WebhookConfigProps {
  workflowId: string | undefined;
  webhookRateLimit: number;
  onRateLimitChange: (limit: number) => void;
  hasToken: boolean;
  onTestPayloadCaptured?: (payload: Record<string, unknown>) => void;
}

export const WebhookConfig = ({
  workflowId,
  webhookRateLimit,
  onRateLimitChange,
  hasToken,
  onTestPayloadCaptured,
}: WebhookConfigProps) => {
  const { toast } = useToast();
  const generateTokenMutation = useGenerateWebhookToken();
  const { data: testPayloadData, refetch: refetchTestPayload } = useWebhookTestPayload(workflowId);

  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(true);
  const [copied, setCopied] = useState<'url' | 'token' | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [listenStartedAt, setListenStartedAt] = useState<string | null>(null);

  const handleGenerateToken = useCallback(async () => {
    if (!workflowId) return;
    try {
      const result = await generateTokenMutation.mutateAsync(workflowId);
      setGeneratedToken(result.token);
      setWebhookUrl(result.webhook_url);
      setShowToken(true);
      toast({
        variant: 'success',
        title: 'Token generated',
        description: "Save this token — it won't be shown again.",
      });
    } catch {
      toast({ variant: 'destructive', title: 'Failed to generate token' });
    }
  }, [workflowId, generateTokenMutation, toast]);

  const handleCopy = useCallback(async (text: string, type: 'url' | 'token') => {
    await navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  useEffect(() => {
    if (!isListening || !workflowId) return;
    const interval = setInterval(async () => {
      const result = await refetchTestPayload();
      const payload = result.data?.test_payload as
        | { captured_at?: string; [key: string]: unknown }
        | null
        | undefined;
      if (payload?.captured_at && payload.captured_at !== listenStartedAt) {
        setIsListening(false);
        setListenStartedAt(null);
        onTestPayloadCaptured?.(payload as Record<string, unknown>);
        toast({
          variant: 'success',
          title: 'Test request captured',
          description: 'Payload structure is now available in variable autocomplete.',
        });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [isListening, workflowId, listenStartedAt, refetchTestPayload, onTestPayloadCaptured, toast]);

  const inputClass =
    'border-input bg-background focus-visible:ring-ring rounded-md border px-2 py-1 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none';

  return (
    <div className="flex flex-wrap items-center gap-2">
      {(webhookUrl || (workflowId && hasToken)) && (
        <div className="flex items-center gap-1">
          <code className="bg-muted max-w-[200px] truncate rounded px-1.5 py-0.5 text-[10px]">
            {webhookUrl ?? `POST /api/workflows/${workflowId}/webhook`}
          </code>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => handleCopy(webhookUrl ?? `/api/workflows/${workflowId}/webhook`, 'url')}
          >
            {copied === 'url' ? <Check size={12} /> : <Copy size={12} />}
          </Button>
        </div>
      )}

      {!workflowId ? (
        <span className="text-muted-foreground text-xs">
          Save the workflow first to generate a token
        </span>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerateToken}
          disabled={generateTokenMutation.isPending}
        >
          {generateTokenMutation.isPending ? (
            <Loader2 size={12} className="mr-1 animate-spin" />
          ) : (
            <RefreshCw size={12} className="mr-1" />
          )}
          {hasToken ? 'Regenerate Token' : 'Generate Token'}
        </Button>
      )}

      {generatedToken && (
        <div className="flex items-center gap-1">
          <code className="bg-muted max-w-[160px] truncate rounded px-1.5 py-0.5 text-[10px]">
            {showToken ? generatedToken : '••••••••••••••••'}
          </code>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setShowToken((v) => !v)}
          >
            {showToken ? <EyeOff size={12} /> : <Eye size={12} />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => handleCopy(generatedToken, 'token')}
          >
            {copied === 'token' ? <Check size={12} /> : <Copy size={12} />}
          </Button>
        </div>
      )}

      <div className="flex items-center gap-1">
        <span className="text-muted-foreground text-xs">Rate:</span>
        <input
          type="number"
          min={1}
          max={10000}
          value={webhookRateLimit}
          onChange={(e) => onRateLimitChange(Math.max(1, Number(e.target.value)))}
          className={`${inputClass} w-16`}
          title="Max requests per minute"
        />
        <span className="text-muted-foreground text-xs">/min</span>
      </div>

      {workflowId && hasToken && (
        <Button
          type="button"
          variant={isListening ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            if (!isListening) {
              const current = testPayloadData?.test_payload as
                | { captured_at?: string }
                | null
                | undefined;
              setListenStartedAt(current?.captured_at ?? null);
              setIsListening(true);
            } else {
              setIsListening(false);
              setListenStartedAt(null);
            }
          }}
        >
          {isListening ? (
            <Loader2 size={12} className="mr-1 animate-spin" />
          ) : (
            <Radio size={12} className="mr-1" />
          )}
          {isListening ? 'Listening...' : 'Receive Test Request'}
        </Button>
      )}

      {testPayloadData?.test_payload && (
        <Badge variant="secondary" className="text-[10px]">
          Test payload captured
        </Badge>
      )}
    </div>
  );
};
